import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthBody, AuthToken } from '../../../models/auth.model';
import { UserProfileBody, ReferrerProfile, CandidateProfile, ReferrerBioBody, CandidateBioBody, GetCandidateListResponse, CandidatePreferencesBody, CandidateReferralPreference, RemoveTargetCompany, RemoveTargetRole, GetCandiateReferralPreferences, SeekingStatus, EmploymentStatus } from '../../../models/user.model'
import { WorkExperienceItem, CreateWorkExperienceBody, EditWorkExperienceBody, DeleteWorkExperienceBody, ExperienceItem } from '../../../models/workExperience.model'
import { LinkItem, CreateLinkBody, EditLinkBody, DeleteLinkItem } from '../../../models/link.model'
import { ProfileLinksBody } from '../../../models/link.model'
import { ReferralRequest, CreateReferralRequestBody, ReferralRequestEventLog, EditReferralRequestBody, ViewResponse } from '../../../models/referralRequest.model'
import { ResumeResponse, Resume, ResumeAttacthment } from '../../../models/resume.model'
import { SavedJob, SaveJobBody, JobsResponse, SaveJobResponse, RemoveJobFromBookMarkResponse, RemoveJobFromBookMarkBody, Job, PositionTypesResponse, RoleLevelResponse, JobRoleResponse, JobRole } from '../../../models/job.model'
import { MarkResumeAsPrimaryBody, MarkResumeAsPrimaryResponse, ToggleResumePrivacyBody, ToggleResumePrivacyResponse, DeleteResumeBody, DeleteResumeResponse } from '../../../models/resume.model'
import { Message, Thread, ArchiveThreadBody, CreateMessageBody, CreateThreadBody, PostMessageResponse } from '../../../models/message.model'
import { CompanyData, CompanyResponse, CompanyJobsResponse, CompanyProfileData, Facility } from '../../../models/company.model'
import { NotificationResponse, RemoveNotificationResponse } from '../../../models/notification.model'
import { GetReferrerListResponse } from '../../../models/referrer.model'
import { Location, Region, County } from '../../../models/location.model'
import { Category, Role, Department, BranchRoles, CompanyRole } from '../../../models/roles'
import { EducationLevel, LicenseCode } from '../../../models/education.model'
import { SalaryEntry, SalaryEntriesResponse, AddSalaryEntry, OfferType, EmploymentType } from '../../../models/salary.model'
import { RoleSalaryAggregateStats } from '../../../models/aggregate-stats.model';
import { RoleRanking, FacilityRanking } from '../../..//models/ranking.model';
import { FacilityReview, FacilityReviewBody, FacilityReviewResponse, DeleteReviewResponse, CreateFacilityReviewResponse } from '../../../models/facility-review.model';

import { environment } from '../../../environments/environment';

const apiUrl = `${environment.apiUrl}`

@Injectable({
  providedIn: 'root'
})
export class Api {
  constructor(private http: HttpClient) {
    this.http = http;
  }

  public getHealthCheck() {
    return this.http.get<any>(apiUrl + '/v1/health')
  }

  public authenticate(authBody: AuthBody) {
    return this.http.post<AuthToken>(apiUrl, { authBody });
  }

  public getAllCompanies() {
    return this.http.get<Facility[]>(apiUrl + '/v1/company/companies')
  }

  public getAllIndustries() {
    return this.http.get<any[]>(apiUrl + '/v1/company/industries')
  }

  public getJobsAtCompany(companyId: number, filter: any) {
    return this.http.get<CompanyJobsResponse>(apiUrl + `/v1/company/${companyId}/jobs`, { params: filter })
  }

  public getCompanyById(name: string) {
    return this.http.get<Facility>(apiUrl + `/v1/company/${name}`)
  }

  public getRolesAtCompany(companySlug: string) {
    return this.http.get<CompanyRole[]>(apiUrl + `/v1/company/${companySlug}/roles`)
  }

  public editUserProfile(userProfileBody: UserProfileBody) {
    return this.http.put(apiUrl + '/v1/user/profile/edit', userProfileBody);
  }

  public getJobs(filter?: any) {
    return this.http.get<JobsResponse>(apiUrl + '/v1/job/all', { params: filter });
  }

  public getJobById(id: number) {
    return this.http.get<Job>(apiUrl + `/v1/job/${id}`)
  }

  public getPositionTypes() {
    return this.http.get<any[]>(apiUrl + `/v1/job/positionTypes`)
  }

  public getRoleLevels() {
    return this.http.get<any[]>(apiUrl + `/v1/job/roleLevels`);
  }

  public getJobTypes() {
    return this.http.get<any[]>(apiUrl + `/v1/job/jobTypes`);
  }

  public getEmploymentTypes() {
    return this.http.get(apiUrl + `/v1/job/jobTypes`)
  }

  public saveJobToBookmarks(saveJobBody: SaveJobBody, userId: string) {
    return this.http.post<SaveJobResponse>(apiUrl + `/v1/user/${userId}/bookmarks`, saveJobBody);
  }

  public getUserBookmarks(userId: string) {
    return this.http.get<SavedJob[]>(apiUrl + `/v1/user/${userId}/bookmarks`);
  }

  public removeJobFromBookmarks(userId: string, body: RemoveJobFromBookMarkBody) {
    return this.http.delete<RemoveJobFromBookMarkResponse>(apiUrl + `/v1/user/${userId}/bookmarks`, { body: body });
  }

  public getReferralRequestById(id: number) {
    const params = new HttpParams().set('id', id);
    return this.http.get<ReferralRequest>(apiUrl + `/v1/referralRequest/${id}`, { params });
  }

  public uploadResume(formData: FormData) {
    return this.http.put<ResumeResponse>(apiUrl + '/v1/user/upload/resume', formData);
  }

  public getResumes(userId: string) {
    const params = new HttpParams().set('userId', userId);
    return this.http.get<Resume[]>(apiUrl + `/v1/user/${userId}/resumes`, { params });
  }

  public markResumeAsPrimary(body: MarkResumeAsPrimaryBody) {
    return this.http.put<MarkResumeAsPrimaryResponse>(apiUrl + `/v1/resume/${body.resumeId}/primaryStatus`, body);
  }

  public toggleResumePrivacy(body: ToggleResumePrivacyBody) {
    return this.http.put<ToggleResumePrivacyResponse>(apiUrl + `/v1/resume/${body.resumeId}/visibility`, body);
  }

  public deleteResume(body: DeleteResumeBody) {
    return this.http.put<DeleteResumeResponse>(apiUrl + `/v1/resume/${body.resumeId}/delete`, body);
  }

  public getResumeById(resumeId: number) {
    return this.http.get<Resume>(apiUrl + `/v1/resume/${resumeId}`);
  }

  public getUserThreads() {
    return this.http.get<Thread[]>(apiUrl + `/v1/messages/threads`);
  }

  public getThread(threadId: string) {
    return this.http.get<Thread>(apiUrl + `/v1/threads/${threadId}`);
  }

  public createThread(body: CreateThreadBody) {
    return this.http.post<Thread>(apiUrl + `/v1/threads`, body);
  }

  public getThreadMessages(threadId: string) {
    return this.http.get<Message[]>(apiUrl + `/v1/messages/${threadId}`);
  }

  public sendMessage(body: CreateMessageBody) {
    return this.http.post<PostMessageResponse>(apiUrl + `/v1/messages/${body.threadId}`, body);
  }

  public markMessageAsRead(threadId: string) {
    return this.http.put(apiUrl + `/v1/messages/${threadId}/read`, {});
  }

  public creatDirectMessageThread(body: CreateThreadBody) {
    return this.http.post(apiUrl + `/v1/threads/direct`, body);
  }

  public archiveThread(body: ArchiveThreadBody) {
    return this.http.put(apiUrl + `/v1/threads/${body.threadId}/archive`, body);
  }

  public getReferrerProfile(userId: string) {
    return this.http.get<ReferrerProfile>(apiUrl + `/v1/user/${userId}/referrer`);
  }

  public getCandidateProfile(userId: string) {
    return this.http.get<CandidateProfile>(apiUrl + `/v1/user/${userId}/candidate`);
  }

  public editCandidateProfileBio(userId: string, body: CandidateBioBody) {
    return this.http.put(apiUrl + `/v1/user/${userId}/candidate/profile`, body);
  }

  public editReferrerProfileBio(userId: string, body: ReferrerBioBody) {
    return this.http.put(apiUrl + `/v1/user/${userId}/referrer/bio`, body);
  }

  public editReferrerProfileLinks(userId: string, body: ProfileLinksBody) {
    return this.http.put(apiUrl + `/v1/user/${userId}/referrer/profileLinks`, body);
  }

  public editCandidateProfileLinks(userId: string, body: ProfileLinksBody) {
    return this.http.put(apiUrl + `/v1/user/${userId}/candidate/profileLinks`, body);
  }

  public getWorkExperience(userId: string) {
    return this.http.get<ExperienceItem[]>(apiUrl + `/v1/user/${userId}/workExperience`);
  }

  public addWorkExperience(userId: string, body: CreateWorkExperienceBody) {
    return this.http.post(apiUrl + `/v1/user/${userId}/workExperience`, body);
  }

  public editWorkExperience(userId: string, body: EditWorkExperienceBody) {
    return this.http.put(apiUrl + `/v1/user/${userId}/workExperience`, body);
  }

  public deleteWorkExperience(userId: string, body: DeleteWorkExperienceBody) {
    return this.http.delete(apiUrl + `/v1/user/${userId}/workExperience/${body.id}`);
  }

  public addLinks(userId: string, body: CreateLinkBody) {
    return this.http.post(apiUrl + `/v1/user/${userId}/links`, body);
  }

  public editLink(userId: string, body: EditLinkBody) {
    return this.http.put(apiUrl + `/v1/user/${userId}/links/${body.id}`, body);
  }

  public deleteLink(userId: string, body: DeleteLinkItem) {
    const params = new HttpParams().set('linkId', body.linkId);
    return this.http.delete(apiUrl + `/v1/user/${userId}/links/${body.linkId}`, { params });
  }

  public getNotifications(userId: string, filter: any) {
    return this.http.get<NotificationResponse>(apiUrl + `/v1/notifications/${userId}/unread`, { params: filter });
  }
  public getNotificationHistory(userId: string, filter: any) {
    return this.http.get<NotificationResponse>(apiUrl + `/v1/notifications/${userId}/history`, { params: filter });
  }
  public markNotificationAsRead(notificationIds: string[]) {
    return this.http.patch(apiUrl + `/v1/notifications/read`, notificationIds);
  }

  public removeNotification(notificationIds: string[]) {
    return this.http.put<RemoveNotificationResponse>(apiUrl + `/v1/notifications/delete`, notificationIds);
  }

  public getReferrerList(filter: any) {
    return this.http.get<GetReferrerListResponse>(apiUrl + `/v1/user/referrers`, { params: filter })
  }

  public getCandidateList(filter: any) {
    return this.http.get<GetCandidateListResponse>(apiUrl + `/v1/user/candidates`, { params: filter })
  }

  public getJobRoles() {
    return this.http.get<JobRole[]>(apiUrl + `/v1/roles`)
  }

  public updateReferralPreferences(candidateId: number, body: CandidateReferralPreference) {
    return this.http.put(apiUrl + `/v1/user/${candidateId}/referralPreferences`, body);
  }

  public removeTargetCompany(body: RemoveTargetCompany) {
    return this.http.put(apiUrl + `/v1/user/targetCompany/delete`, body);
  }

  public removeTargetRole(body: RemoveTargetRole) {
    return this.http.put(apiUrl + `/v1/user/targetRole/delete`, body);
  }

  public getCandidateReferralPreferences(candidateId: number) {
    return this.http.get<GetCandiateReferralPreferences>(apiUrl + `/v1/user/candidate/${candidateId}/referralPreferences`);
  }

  public getLocations() {
    return this.http.get<Location[]>(apiUrl + `/v1/locations`);
  }

  public getRegions() {
    return this.http.get<Region[]>(apiUrl + `/v1/regions`);
  }

  public getRoleCategories() {
    return this.http.get<Category[]>(apiUrl + `/v1/roles/categories`);
  }

  public getBranchRoles() {
    return this.http.get<BranchRoles[]>(apiUrl + `/v1/roles`)
  }

  public getProfessionalRoles() {
    return this.http.get<Role[]>(apiUrl + `/v1/roles/professionalRoles`);
  }

  public getCounties() {
    return this.http.get<County[]>(apiUrl + `/v1/counties`);
  }

  public getSalaryEntries(filter: any) {
    return this.http.get<SalaryEntriesResponse>(apiUrl + `/v1/salaries`, { params: filter });
  }

  public addSalaryEntry(body: AddSalaryEntry) {
    return this.http.post(apiUrl + `/v1/salaries`, body);
  }

  public getSalaryOfferTypes() {
    return this.http.get<OfferType[]>(apiUrl + `/v1/salaries/offerTypes`);
  }

  public getRoleSalaryAggregateStats(roleId: string, filter: any) {
    return this.http.get<RoleSalaryAggregateStats | null>(apiUrl + `/v1/salaries/role/${roleId}/stats`, { params: filter });
  }

  public getUserSalaryEntries(userId: string, paginator: any) {
    return this.http.get<SalaryEntriesResponse>(apiUrl + `/v1/salaries/user/${userId}/entries`, { params: paginator })
  }

  public getGeneralEmploymentTypes() {
    return this.http.get<EmploymentType[]>(apiUrl + `/v1/salaries/employmentTypes`);
  }

  public getEducationLevels() {
    return this.http.get<EducationLevel[]>(apiUrl + `/v1/education/levels`);
  }

  public getLicenseCodes() {
    return this.http.get<LicenseCode[]>(apiUrl + `/v1/education/licenses`);
  }

  public getDepartments() {
    return this.http.get<Department[]>(apiUrl + `/v1/roles/departments`);
  }

  public getSeekingStatuses() {
    return this.http.get<SeekingStatus[]>(apiUrl + `/v1/seekingStatuses`);
  }

  public getEmploymentStatuses() {
    return this.http.get<EmploymentStatus[]>(apiUrl + `/v1/user/employmentStatuses`);
  }

  public getResume(resumeId: number) {
    return this.http.get<ResumeAttacthment>(apiUrl + `/v1/resume/${resumeId}`);
  }

  public getRoleRankings() {
    return this.http.get<RoleRanking[]>(apiUrl + `/v1/leaderboard/role`);
  }

  public getFacilityRankings() {
    return this.http.get<FacilityRanking[]>(apiUrl + `/v1/leaderboard/facility`);
  }

  public getUserLastSalarySubmissionFlag(userId: string) {
    return this.http.get<boolean>(apiUrl + `/v1/user/${userId}/lastsubmissionDate`);
  }

  public getFacilityReviews(facilityId: string, filter?: any) {
    return this.http.get<FacilityReviewResponse>(apiUrl + `/v1/review/${facilityId}/reviews`, { params: filter });
  }

  public getFacilityReview(reviewId: string) {
    return this.http.get<FacilityReview>(apiUrl + `/v1/review/${reviewId}`);
  }

  public postFacilityReview(facilityId: string, body: any) {
    return this.http.post<CreateFacilityReviewResponse>(apiUrl + `/v1/review`, body);
  }

  public updateFacilityReview(reviewId: string, body: any) {
    return this.http.put<FacilityReview>(apiUrl + `/v1/review/${reviewId}/edit`, body);
  }

  public deleteFacilityReview(reviewId: string) {
    return this.http.delete<DeleteReviewResponse>(apiUrl + `/v1/review/${reviewId}/remove`);
  }

}
